// Files attached to a message, shown as square cards under it, and the viewer that opens when a card is clicked.
import React, { useEffect, useState } from 'react';
import { X, Download } from 'lucide-react';
import api from '../../services/api';

const KINDS = [
  { test: (n, t) => /pdf$/i.test(t) || /\.pdf$/i.test(n), label: 'PDF', bg: 'var(--lyra-color-danger-bg)', fg: 'var(--lyra-color-danger)' },
  { test: (n, t) => /(wordprocessingml|msword)/i.test(t) || /\.docx?$/i.test(n), label: 'DOC', bg: 'var(--app-primary-soft)', fg: 'var(--app-primary-deep)' },
  { test: (n, t) => /(spreadsheetml|ms-excel)/i.test(t) || /\.(xlsx?|csv)$/i.test(n) || /csv/i.test(t), label: 'XLS', bg: 'var(--lyra-color-success-bg)', fg: 'var(--lyra-color-success)' },
  { test: (n, t) => /^image\//i.test(t) && !/svg/i.test(t), label: 'IMG', bg: 'var(--lyra-color-warning-bg)', fg: 'var(--lyra-color-warning)' },
  { test: (n, t) => /^text\//i.test(t) || /\.(txt|md|json|log|xml|ya?ml|js|ts|py)$/i.test(n), label: 'TXT', bg: 'var(--app-surface-3)', fg: 'var(--app-text-2)' },
];
export function kindOf(file) {
  const k = KINDS.find((x) => x.test(file.name || '', file.contentType || ''));
  if (k) return k;
  const ext = ((file.name || '').split('.').pop() || 'FILE').slice(0, 4).toUpperCase();
  return { label: ext || 'FILE', bg: 'var(--app-surface-3)', fg: 'var(--app-text-2)' };
}

const size = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n >= 1024 ? `${Math.round(n / 1024)} KB` : `${n || 0} B`);

export function AttachmentCards({ files, onOpen }) {
  if (!files || !files.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end', maxWidth: '100%' }} aria-label="Attached files">
      {files.map((f, i) => {
        const k = kindOf(f);
        return (
          <button key={`${f.id || f.name}-${i}`} type="button" onClick={() => onOpen(f)} title={`${f.name} (${size(f.size)}). Click to open`} aria-label={`Open ${f.name}`}
            style={{ width: 96, height: 96, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'space-between', padding: 8, borderRadius: 12, border: '0.5px solid var(--lyra-color-border)', background: 'var(--lyra-color-surface)', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', overflow: 'hidden' }}>
            <span style={{ alignSelf: 'flex-start', fontSize: 13, fontWeight: 800, letterSpacing: '.04em', padding: '3px 8px', borderRadius: 7, background: k.bg, color: k.fg }}>{k.label}</span>
            <span style={{ fontSize: 11, lineHeight: 1.3, color: 'var(--lyra-color-text)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>{f.name}</span>
          </button>
        );
      })}
    </div>
  );
}

const TEXT_LIMIT = 1024 * 1024;

/** Opens the file: the copy this browser still has if any, else the server's (kept for an hour). */
export function AttachmentViewer({ file, onClose }) {
  const [state, setState] = useState({ phase: 'loading' });
  useEffect(() => {
    let live = true; let objectUrl = null;
    (async () => {
      try {
        let blob = file.blob instanceof Blob ? file.blob : null;
        if (!blob) {
          const r = await api.get(`/api/attachments/${encodeURIComponent(file.id)}/content`, { responseType: 'blob' });
          blob = r.data;
        }
        const k = kindOf(file);
        const head = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
        const isPdf = String.fromCharCode(...head) === '%PDF-';
        if (k.label === 'PDF' && isPdf) {
          // re-typed, so whatever was declared at upload can never make the browser run it as something else
          objectUrl = URL.createObjectURL(new Blob([await blob.arrayBuffer()], { type: 'application/pdf' }));
          if (live) setState({ phase: 'pdf', url: objectUrl });
        } else if (k.label === 'IMG') {
          objectUrl = URL.createObjectURL(new Blob([await blob.arrayBuffer()], { type: blob.type && blob.type.startsWith('image/') ? blob.type : 'image/png' }));
          if (live) setState({ phase: 'image', url: objectUrl });
        } else if (k.label === 'TXT' && blob.size <= TEXT_LIMIT) {
          const text = await blob.text();
          if (live) setState({ phase: 'text', text });
        } else {
          objectUrl = URL.createObjectURL(blob);
          if (live) setState({ phase: 'none', url: objectUrl });
        }
      } catch (e) {
        const gone = e && e.response && e.response.status === 404;
        if (live) setState({ phase: 'error', message: gone ? 'This file is no longer available. Attached files are kept for an hour; attach it again to open it.' : 'Could not open this file.' });
      }
    })();
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [file]);
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  const k = kindOf(file);
  const download = state.url || (state.phase === 'text' ? URL.createObjectURL(new Blob([state.text], { type: 'text/plain' })) : null);
  return (
    <div role="dialog" aria-label={`Viewing ${file.name}`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(8,12,24,.62)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: 'min(980px, 100%)', height: 'min(88vh, 900px)', display: 'flex', flexDirection: 'column', background: 'var(--lyra-color-surface)', border: '0.5px solid var(--lyra-color-border)', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '0.5px solid var(--lyra-color-border)', flexShrink: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.04em', padding: '3px 8px', borderRadius: 7, background: k.bg, color: k.fg }}>{k.label}</span>
          <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: 'var(--lyra-color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</div>
          <span style={{ fontSize: 12, color: 'var(--lyra-color-text-muted)' }}>{size(file.size)}</span>
          {download && <a href={download} download={file.name} aria-label="Download" title="Download" style={{ display: 'flex', color: 'var(--app-text-2)', padding: 6 }}><Download size={16} /></a>}
          <button type="button" onClick={onClose} aria-label="Close viewer" autoFocus style={{ display: 'flex', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--app-text-2)', padding: 6 }}><X size={18} /></button>
        </div>
        <div style={{ flex: 1, minHeight: 0, background: 'var(--lyra-color-surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}>
          {state.phase === 'loading' && <div style={{ color: 'var(--lyra-color-text-muted)', fontSize: 13 }}>Opening…</div>}
          {state.phase === 'error' && <div role="alert" style={{ color: 'var(--lyra-color-text-muted)', fontSize: 13.5, maxWidth: 420, textAlign: 'center', lineHeight: 1.5, padding: 20 }}>{state.message}</div>}
          {state.phase === 'pdf' && <iframe title={file.name} src={state.url} style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }} />}
          {state.phase === 'image' && <img src={state.url} alt={file.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
          {state.phase === 'text' && <pre style={{ margin: 0, padding: 16, alignSelf: 'stretch', width: '100%', fontSize: 12.5, lineHeight: 1.55, color: 'var(--lyra-color-text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'var(--lyra-font-mono)' }}>{state.text}</pre>}
          {state.phase === 'none' && <div style={{ color: 'var(--lyra-color-text-muted)', fontSize: 13.5, textAlign: 'center', padding: 20 }}>No preview for this file type. Use the download button.</div>}
        </div>
      </div>
    </div>
  );
}
